import React from 'react';
import 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import { Box } from '@mui/material';
import { InterventionSupplierType } from '../../../api-service/clients';
import { TypographySettings } from '../../../components/config';
import { formatNumber } from '../../../utilities';
import { externalCostColor, internalCostColor } from '../intervention-ladder/intervention-ladder-colors';

const textColor = '#5b6b73';
const darkTextColor = '#16323d';
const gridColor = 'rgba(24, 76, 97, 0.08)';
const font = TypographySettings.DefaultFontFamily;

interface TooltipItem {
  index: number;
  datasetIndex: number;
  value: string;
}

interface TooltipData {
  labels: string[];
  datasets: { label: string }[];
}

interface DataLabelContext {
  dataIndex: number;
  dataset: { totals?: number[] };
}

interface ChartElement {
  _index: number;
  _datasetIndex: number;
}

interface ClickableChart {
  getElementAtEvent: (event: MouseEvent) => ChartElement[];
  getElementsAtXAxis: (event: MouseEvent) => ChartElement[];
}

const datasetTypes = [InterventionSupplierType.Internal, InterventionSupplierType.External];

const axisTitle = { display: true, fontSize: 12, fontStyle: 'bold', fontColor: textColor, fontFamily: font };

const options = {
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  layout: { padding: { top: 24 } },
  hover: { mode: 'nearest', intersect: true, onHover: showPointer },
  scales: {
    xAxes: [
      {
        stacked: true,
        gridLines: { display: false, drawBorder: false },
        ticks: { autoSkip: false, maxRotation: 0, fontSize: 13, fontStyle: 'bold', fontColor: darkTextColor, fontFamily: font },
        scaleLabel: { ...axisTitle, labelString: 'Paragraf' },
      },
    ],
    yAxes: [
      {
        stacked: true,
        gridLines: { color: gridColor, zeroLineColor: gridColor, drawBorder: false, drawTicks: false },
        ticks: { beginAtZero: true, precision: 0, padding: 8, maxTicksLimit: 5, fontSize: 12, fontColor: textColor, fontFamily: font },
        scaleLabel: { ...axisTitle, labelString: 'Antal leverandører' },
      },
    ],
  },
  tooltips: {
    backgroundColor: darkTextColor,
    displayColors: false,
    xPadding: 10,
    yPadding: 8,
    titleFontFamily: font,
    bodyFontFamily: font,
    callbacks: {
      title: (items: TooltipItem[], data: TooltipData) => data.labels[items[0].index],
      label: (item: TooltipItem, data: TooltipData) => `${data.datasets[item.datasetIndex].label} ${formatNumber(Number(item.value))}`,
    },
  },
  plugins: {
    datalabels: {
      // Only the top of each stack carries the paragraph's total.
      display: (context: DataLabelContext) => (context.dataset.totals?.[context.dataIndex] ?? 0) > 0,
      formatter: (_: number, context: DataLabelContext) => formatNumber(context.dataset.totals?.[context.dataIndex] ?? 0),
      anchor: 'end',
      align: 'end',
      offset: 2,
      color: darkTextColor,
      font: { size: 12, weight: 600, family: font },
    },
  },
};

function showPointer(event: MouseEvent, elements: ChartElement[]) {
  const target = event.target as HTMLElement | null;
  if (target) {
    target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
  }
}

const barShape = { maxBarThickness: 64, categoryPercentage: 0.5 };

export interface InterventionSupplierChartProps {
  labels: string[];
  internal: number[];
  external: number[];
  onSelect: (paragraphIndex: number, type?: InterventionSupplierType) => void;
}

export function InterventionSupplierChart({ labels, internal, external, onSelect }: InterventionSupplierChartProps) {
  // A segment chooses its paragraph and type, anywhere else in a paragraph's column only the paragraph.
  function onClick(this: ClickableChart, event: MouseEvent) {
    const segment = this.getElementAtEvent(event)[0];
    if (segment) {
      onSelect(segment._index, datasetTypes[segment._datasetIndex]);
      return;
    }
    const column = this.getElementsAtXAxis(event)[0];
    if (column) {
      onSelect(column._index);
    }
  }

  return (
    <Box sx={{ height: 300 }}>
      <Bar
        data={{
          labels,
          datasets: [
            { label: 'Interne leverandører', data: internal, backgroundColor: internalCostColor, hoverBackgroundColor: internalCostColor, ...barShape },
            {
              label: 'Eksterne leverandører',
              data: external,
              backgroundColor: externalCostColor,
              hoverBackgroundColor: externalCostColor,
              ...barShape,
              totals: internal.map((value, index) => value + external[index]),
            },
          ],
        }}
        options={{ ...options, onClick }}
      />
    </Box>
  );
}
